import React, { useEffect, useState } from 'react';
import { Button } from './button';
import { X, Maximize2, Users } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { useToast } from './use-toast';
import { trackMapConfigEvents } from '../utils/ga4Events';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Adapter shape used inside the slideshow regardless of source.
//   { id, kind: 'predefined' | 'shared', name, imageUrl, settings?,
//     useCount?, sharedBy? }
// Predefined items carry inline settings (loaded from /assets/settings.json).
// Shared items carry settings:undefined and are fetched on apply via
// /api/shared_styles/<id> so the listing payload stays small.

const StaticImageSlideshow = ({ onSettingsChange, children }) => {
  const [items, setItems] = useState([]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [appliedId, setAppliedId] = useState(null);
  const [applying, setApplying] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load both sources in parallel — predefined templates from the
      // bundled JSON (always succeeds), shared styles from the API
      // (may fail offline / pre-deploy and we just degrade gracefully).
      const predefinedP = fetch('/assets/settings.json')
        .then((r) => r.json())
        .then((d) => (d.templates || []).map((t) => ({
          id: `predef:${t.id}`,
          kind: 'predefined',
          name: t.name,
          imageUrl: t.imageUrl,
          settings: t.settings,
        })))
        .catch(() => []);

      const sharedP = fetch(`${API_URL}/shared_styles?limit=50`)
        .then((r) => (r.ok ? r.json() : { styles: [] }))
        .then((d) => (d.styles || []).map((s) => ({
          id: `shared:${s.id}`,
          rawId: s.id,
          kind: 'shared',
          name: s.style_name || 'Shared style',
          imageUrl: `${API_URL}/shared_styles/${s.id}/preview`,
          useCount: s.use_count || 0,
          sharedBy: s.display_name || 'Anonymous',
        })))
        .catch(() => []);

      const [predefined, shared] = await Promise.all([predefinedP, sharedP]);
      if (cancelled) return;
      // Predefined first (curator order from settings.json), shared after
      // sorted by use_count desc — the popularity sort the user asked for.
      shared.sort((a, b) => (b.useCount || 0) - (a.useCount || 0));
      setItems([...predefined, ...shared]);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEnlargeImage = (e, item) => {
    e.stopPropagation();
    setEnlargedImage({ name: item.name, imageUrl: item.imageUrl });
  };

  const handleApplySettings = async () => {
    const item = items.find((i) => i.id === selectedId);
    if (!item) return;
    if (item.kind === 'predefined') {
      onSettingsChange(item.settings, item.name);
      setAppliedId(item.id);
      setSelectedId(null);
      trackMapConfigEvents.styleSelected(item.name);
      return;
    }
    // Shared: settings live behind /api/shared_styles/<id> and the GET
    // also bumps use_count on the backend.
    setApplying(true);
    try {
      const r = await fetch(`${API_URL}/shared_styles/${item.rawId}`);
      if (!r.ok) throw new Error('Failed to fetch shared style');
      const data = await r.json();
      const settings = data?.style?.settings;
      if (!settings) throw new Error('Style has no settings');
      onSettingsChange(settings, item.name);
      setAppliedId(item.id);
      setSelectedId(null);
      trackMapConfigEvents.styleSelected(item.name);
    } catch (e) {
      toast({
        title: 'Could not apply style',
        description: e.message,
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="relative space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2 lg:gap-4 w-full max-h-[calc(1.8*10rem)] md:max-h-[calc(3*10rem)] lg:max-h-[calc(4*10rem)] overflow-y-auto p-2 mb-4">
        {items.map((image) => (
          <div
            key={image.id}
            className={`aspect-[4/3] relative group cursor-pointer rounded-xl
              ${selectedId === image.id ? 'ring-2 ring-blue-500' : ''}
              ${appliedId === image.id ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setSelectedId(image.id)}
          >
            <img
              src={image.imageUrl}
              alt={image.name}
              className="w-full h-full object-cover rounded-lg border border-gray-200 rounded-xl"
            />
            {/* Marker on user-shared styles so they're distinguishable
                from the curated bundled templates. */}
            {image.kind === 'shared' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="absolute top-1 left-1 bg-white text-gray-900 border-2 border-red-500 rounded-full h-6 w-6 flex items-center justify-center shadow-md">
                      <Users className="h-3 w-3" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Shared by {image.sharedBy}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 rounded-xl
                        group-hover:bg-opacity-70 transition-colors">
              <div className="flex justify-between items-center">
                <p className="text-xs truncate">
                  {image.name}
                  {image.kind === 'shared' && image.useCount > 0 && (
                    <span className="text-gray-300"> · used {image.useCount} times</span>
                  )}
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-white hover:text-blue-400"
                        onClick={(e) => handleEnlargeImage(e, image)}
                      >
                        <Maximize2 className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View larger image</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-center space-x-2 items-center">
        <div className="w-4 h-4 flex items-center justify-center">
          {selectedId && (
            <div className="text-gray-700 animate-wiggle">
              ➜
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="black"
          onClick={handleApplySettings}
          disabled={!selectedId || applying}
          className={`
            transition-all duration-300 border-0 text-xs lg:text-md
            ${selectedId ?
              'rounded-3xl' :
              'rounded-3xl bg-gray-100 text-gray-400'
            }
          `}
        >
          {applying ? 'Applying…' : 'Select Style'}
        </Button>
        <div className="space-x-2 flex">
          {children}
        </div>
      </div>

      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-3xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{enlargedImage.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEnlargedImage(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-6">
              <img
                src={enlargedImage.imageUrl}
                alt={`Enlarged preview for ${enlargedImage.name}`}
                className="w-full h-auto rounded-lg shadow-2xl shadow-black/50"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaticImageSlideshow;